import styled from 'styled-components';
import { countries } from '@app/constants';
import { BuildJsonType } from '@bb/domain/dist/typeHelpers';
import { OrgAddress } from '@bb/domain/dist/types';
import React from 'react';
import { formatPhoneNumber } from 'utils';
import { Flex, FlexColumn, LightDetailText } from '../styles';

type SelectOrgAddress = Pick<
  BuildJsonType<OrgAddress>,
  'line1' | 'line2' | 'city' | 'province' | 'country' | 'postalCode'
> & { orgName?: string };

type NullableOrgAddress = {
  [P in keyof SelectOrgAddress]: SelectOrgAddress[P] | null;
};

type ContactInfoProps = {
  type: 'email' | 'office' | 'mobile' | 'address' | 'id';
  label?: string;
  data?: string | null;
  address?: NullableOrgAddress;
  doNotRelease?: boolean;
  includeAddressName?: boolean;
  styles?: {
    label?: any;
    addressText?: any;
  };
};

const ContactInfo: React.FC<ContactInfoProps> = ({
  type,
  label,
  data,
  doNotRelease,
  address,
  includeAddressName,
  styles,
}) => {
  if (!type || (!data && !address)) return null;

  if (['mobile', 'office'].includes(type)) {
    // eslint-disable-next-line no-param-reassign
    data = formatPhoneNumber(data);
  }

  return (
    <Flex style={{ paddingRight: '30px' }}>
      <PadRight style={{ ...styles?.label }} $type={type}>
        {label || type}
      </PadRight>{' '}
      <FlexColumn>
        {type === 'email' && <a href={`mailto:${data}`}>{data}</a>}
        {(type === 'mobile' || type === 'office') && (
          <a href={`tel:${data}`}>{data}</a>
        )}
        {type === 'mobile' && doNotRelease && (
          <LightDetailText>(Do not release)</LightDetailText>
        )}
        {type === 'address' && address && (
          <FlexColumn>
            {includeAddressName && address.orgName && (
              <StyledAddressName>{address.orgName}</StyledAddressName>
            )}
            <Flex style={{ ...styles?.addressText }}>{address.line1}</Flex>
            <Flex style={{ ...styles?.addressText }}>{address.line2}</Flex>
            <Flex style={{ ...styles?.addressText }}>
              {address.city}, {address.province} {address.postalCode}
            </Flex>
            {address.country && (
              <Flex style={{ ...styles?.addressText }}>
                {
                  countries.find(({ value }) => value === address.country)
                    ?.label
                }
              </Flex>
            )}
          </FlexColumn>
        )}
        {type === 'id' && (
          <FlexColumn>
            <Flex>{data}</Flex>
          </FlexColumn>
        )}
      </FlexColumn>
    </Flex>
  );
};

export default ContactInfo;

const PadRight = styled.div<{ $type: string }>`
  ${({ $type }) =>
    $type === 'id'
      ? `text-transform: uppercase;`
      : `text-transform: capitalize;`}
  padding-right: 30px;
`;

const StyledAddressName = styled(Flex)`
  font-weight: 600;
`;
